import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from '@ami/react/core';
import { LocationSearchInput } from '@ami/react/core';
import {
  QuoteInstallationLocationOption,
  quoteInstallationLocationType,
} from '../../../quotes/quoteInstallationInformation';
import { QuoteInstallation } from '../../../quotes/otherQuoteEntities';
import { formatPrice } from '../../../quotes/priceFormatter';
import { calculateTravelExpense } from '../../../calculateTravelExpense';
import { Spinner } from '@ami/react/core';
import { NotificationManager } from 'react-notifications';
import classNames from 'classnames';

export default class ReviewQuoteInstallationTravel extends React.Component {
  static propTypes = {
    priceZoneId: PropTypes.string.isRequired,
    dealerGroupId: PropTypes.string.isRequired,
    basePlantLocation: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired,
    locations: PropTypes.arrayOf(
      PropTypes.instanceOf(QuoteInstallationLocationOption)
    ).isRequired,
    installation: PropTypes.instanceOf(QuoteInstallation).isRequired,
    onInstallationUpdate: PropTypes.func.isRequired,
    msrpClassNames: PropTypes.string.isRequired,
    dNetClassNames: PropTypes.string.isRequired,
    isoCurrencySymbol: PropTypes.string.isRequired,
  };

  state = {
    calculatingTravelExpense: false,
  };

  getLocationOption = (locationId) =>
    this.props.locations.find((x) => x.id === locationId);

  isCustomLocationSelected = () => {
    const { locationId } = this.props.installation;
    const option = this.getLocationOption(locationId);
    if (!option) return false;
    return option.type === quoteInstallationLocationType.other;
  };

  updateLocation = async (locationId, customLocation = null) => {
    const copy = this.props.installation.clone();
    if (!locationId) {
      this.props.onInstallationUpdate(copy.setLocation(null));
      return;
    }

    const locationOption = this.getLocationOption(locationId);
    if (locationOption.type === quoteInstallationLocationType.amiPlant) {
      this.props.onInstallationUpdate(copy.setAmiPlantLocation(locationId));
      return;
    }

    const location =
      locationOption.type === quoteInstallationLocationType.dealerBranch
        ? locationOption.location
        : customLocation;
    if (!location) {
      //happens if custom is selected but not location defined yet
      this.props.onInstallationUpdate(copy.setLocation(locationId));
      return;
    }
    this.setState({ calculatingTravelExpense: true });
    try {
      const travelExpense = await calculateTravelExpense(
        this.props.priceZoneId,
        this.props.dealerGroupId,
        this.props.basePlantLocation,
        location
      );

      return this.props.onInstallationUpdate(
        copy.setLocation(
          locationId,
          locationOption.type,
          location,
          travelExpense
        )
      );
    } catch (e) {
      NotificationManager.error(
        'Service unavailable',
        'Unable to calculate distance',
        7 * 1000
      );
    } finally {
      this.setState({ calculatingTravelExpense: false });
    }
  };

  render() {
    const {
      locations,
      installation,
      msrpClassNames,
      dNetClassNames,
      isoCurrencySymbol,
    } = this.props;
    const { calculatingTravelExpense } = this.state;
    return (
      <tr>
        <td className="text-start">
          <Fragment>
            {calculatingTravelExpense ? (
              <span>Calculating travel charge...</span>
            ) : (
              <span>Install location</span>
            )}
            <Spinner show={calculatingTravelExpense} />
          </Fragment>
          <Dropdown
            className="mt-2"
            placeholder="Select installation location"
            items={locations}
            onChange={(locationId) => this.updateLocation(locationId)}
            selectedId={installation.locationId}
            required
          />
          {this.isCustomLocationSelected() && (
            <LocationSearchInput
              className="pt-2 mb-0"
              placeholder="Custom location"
              location={installation.location}
              onLocationChange={(location) =>
                this.updateLocation(installation.locationId, location)
              }
            />
          )}
        </td>
        <td data-title="Price" className="price-breakdown">
          <div className="row price-heading-mobile">
            <div className="col-6"></div>
            <div className={classNames(msrpClassNames, 'price-heading')}>
              MSRP
            </div>
            <div className={classNames(dNetClassNames, 'price-heading')}>
              D. NET
            </div>
          </div>
          {installation.travelExpense && (
            <div className="row total-price">
              <div className="col-6">Travel Charge ({isoCurrencySymbol})</div>
              <div className={msrpClassNames}>
                {formatPrice(installation.travelExpense.msrp)}
              </div>
              <div className={dNetClassNames}>
                {formatPrice(installation.travelExpense.dNet)}
              </div>
            </div>
          )}
        </td>
      </tr>
    );
  }
}
